import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, PackageSearch, Mail, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import { useBrand } from "../../context/BrandContext";
import { Logo } from "./Logo";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];
const slug = (l) => l.toLowerCase().replace(/\s+/g, "-");
const QUOTE_TO = { pathname: "/", hash: "#quote" };

const UtilityBar = ({ b }) => (
  <div className="hidden lg:block bg-[#F7F8FA] border-b border-slate-200">
    <div className="container-x h-8 flex items-center justify-between font-mono text-[11px] tracking-wider text-slate-600">
      <div className="flex items-center gap-6">
        <span data-testid="utility-usdot">USDOT {b.usdot}</span>
        <span>{b.mc}</span>
        <span className="text-brand-600">Licensed & Insured</span>
      </div>
      <div className="flex items-center gap-6">
        <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{b.address_full}</span>
        <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-brand-600" data-testid="utility-email-link"><Mail className="w-3 h-3" />{b.email}</a>
      </div>
    </div>
  </div>
);

const MobileMenu = ({ b }) => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="lg:hidden w-10 h-10 grid place-items-center border border-slate-300 text-slate-800" data-testid="mobile-menu-button" aria-label="Open menu">
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-white border-slate-200 text-slate-900 w-[86vw] max-w-sm" data-testid="mobile-menu">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <nav className="mt-10 flex flex-col">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end onClick={() => setOpen(false)} data-testid={`mobile-nav-${slug(n.label)}`}
              className={({ isActive }) => `font-display text-2xl uppercase font-bold py-3 border-b border-slate-200 ${isActive ? "text-brand-600" : "text-slate-900"}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/track" onClick={() => setOpen(false)} className="btn-ghost mt-8" data-testid="mobile-nav-track">Track Shipment</Link>
          <Link to={QUOTE_TO} onClick={() => setOpen(false)} className="btn-amber mt-3" data-testid="mobile-nav-quote">Get a Quote</Link>
          <a href={`tel:${b.phone_tel}`} className="mt-6 font-mono text-brand-600 text-sm" data-testid="mobile-nav-phone">{b.phone_display}</a>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const b = useBrand();
  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <UtilityBar b={b} />
      <div className="backdrop-blur-xl bg-white/90 border-b border-slate-200">
        <div className="container-x h-16 lg:h-20 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-link-${slug(n.label)}`}
                className={({ isActive }) => `font-display uppercase tracking-wider text-[15px] font-semibold transition-colors ${isActive ? "text-brand-600" : "text-slate-700 hover:text-slate-900"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a href={`tel:${b.phone_tel}`} data-testid="header-phone-link" className="hidden xl:flex items-center gap-2 mr-2 text-slate-900 hover:text-brand-600 transition-colors">
              <span className="w-9 h-9 grid place-items-center border border-amber-500/60 text-brand-600"><Phone className="w-4 h-4" /></span>
              <span className="font-mono text-sm">{b.phone_display}</span>
            </a>
            <a href={`tel:${b.phone_tel}`} data-testid="header-phone-icon" aria-label="Call us" className="xl:hidden w-10 h-10 grid place-items-center border border-amber-500/60 text-brand-600">
              <Phone className="w-4 h-4" />
            </a>
            <Link to="/track" className="btn-ghost h-10 px-3 sm:px-4 text-sm" data-testid="header-track-button" aria-label="Track Shipment">
              <PackageSearch className="w-4 h-4" /><span className="hidden sm:inline">Track</span><span className="hidden md:inline -ml-1">&nbsp;Shipment</span>
            </Link>
            <Link to={QUOTE_TO} className="btn-amber h-10 px-3 sm:px-4 text-sm" data-testid="header-quote-button">
              <span className="sm:hidden">Quote</span><span className="hidden sm:inline">Get a Quote</span>
            </Link>
            <MobileMenu b={b} />
          </div>
        </div>
      </div>
    </header>
  );
};
